import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSArrowFunctions: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Arrow Functions</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    A shorter way to write functions — and, more importantly, a different way they handle <code>this</code>. The concise syntax is the easy part; lexical <code>this</code> is what interviews are really about.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="13 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Before ES6, every function used the traditional syntax — fine on its own, but verbose once you have callbacks, event handlers, promise chains, and array methods everywhere:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `users.map(function (user) {\n  return user.name;\n});` }]} defaultLanguage="javascript" id="af-problem" />
   <Para>For simple operations this is a lot of boilerplate. Developers wanted less typing, cleaner syntax, and better readability — so ES6 introduced arrow functions.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Arrow functions</Mark> provide a shorter syntax for writing functions, using the <Code>=&gt;</Code> token.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `// traditional\nfunction add(a, b) {\n  return a + b;\n}\n\n// arrow\nconst add = (a, b) => {\n  return a + b;\n};` }]} defaultLanguage="javascript" id="af-definition" />
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why They Exist</SectionHeading>
   <div className="flex flex-wrap gap-2">{['Less typing', 'Cleaner callbacks', 'Better readability', 'Lexical this'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
   <Para>That last one — how they handle <Code>this</Code> — is the most important and the most asked about.</Para>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>An arrow function is two things at once: a <Mark>compact function</Mark> and a function with <Mark>lexical this</Mark>. Most developers learn only the first; interviewers ask about the second.</Para>
  </section>

  {/* 5. Basic syntax */}
  <section className="mb-12">
   <SectionHeading n={5}>Basic Syntax</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// traditional\nfunction greet(name) {\n  return "Hello " + name;\n}\n\n// arrow\nconst greet = (name) => {\n  return "Hello " + name;\n};` }]} defaultLanguage="javascript" id="af-basic" />
  </section>

  {/* 6. Single param */}
  <section className="mb-12">
   <SectionHeading n={6}>Single-Parameter Shortcut</SectionHeading>
   <Para>With exactly one parameter, the parentheses are optional:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const greet = name => {\n  return "Hello " + name;\n};` }]} defaultLanguage="javascript" id="af-single" />
  </section>

  {/* 7. Implicit return */}
  <section className="mb-12">
   <SectionHeading n={7}>Implicit Return</SectionHeading>
   <Para>Drop the braces and the <Code>return</Code>, and the expression becomes the return value:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// explicit return\nconst square = (num) => {\n  return num * num;\n};\n\n// implicit return — same thing\nconst square = num => num * num;\n\nsquare(5); // 25` }]} defaultLanguage="javascript" id="af-implicit" />
   <Output>25</Output>
  </section>

  {/* 8. Returning objects */}
  <section className="mb-12">
   <SectionHeading n={8}>Returning Objects (Common Trap)</SectionHeading>
   <Para>An implicit return of an object literal must be wrapped in parentheses, otherwise <Code>{`{}`}</Code> is read as the function body:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// WRONG — {} is treated as the function body\nconst getUser = () => { name: "Sachin" };\ngetUser(); // undefined\n\n// CORRECT — wrap the object in ()\nconst getUser = () => ({ name: "Sachin" });\ngetUser(); // { name: "Sachin" }` }]} defaultLanguage="javascript" id="af-object" />
  </section>

  {/* 9. Array methods */}
  <section className="mb-12">
   <SectionHeading n={9}>With Array Methods</SectionHeading>
   <Para>This is where arrow functions shine — concise callbacks for map, filter, reduce, etc.:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [1, 2, 3, 4];\n\nconst doubled = numbers.map(num => num * 2);        // [2, 4, 6, 8]\nconst evens = numbers.filter(num => num % 2 === 0); // [2, 4]` }]} defaultLanguage="javascript" id="af-arrays" />
  </section>

  {/* 10. this difference */}
  <section className="mb-12">
   <SectionHeading n={10}>The Big Difference: this</SectionHeading>
   <Para>A traditional method gets its own <Code>this</Code> (the object it&apos;s called on). An arrow function does <Mark>not</Mark> — it inherits <Code>this</Code> from the surrounding scope:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// traditional method — works\nconst user = {\n  name: "Sachin",\n  greet: function () {\n    console.log(this.name);\n  },\n};\nuser.greet(); // "Sachin"\n\n// arrow as method — breaks\nconst user2 = {\n  name: "Sachin",\n  greet: () => {\n    console.log(this.name); // this is NOT user2\n  },\n};\nuser2.greet(); // undefined` }]} defaultLanguage="javascript" id="af-this" />
   <Callout tone="warning">Arrow functions don&apos;t create their own <Code>this</Code>; they inherit it from where they were <Mark>defined</Mark>. This is called <Mark>lexical this</Mark>.</Callout>
  </section>

  {/* 11. Lexical this */}
  <section className="mb-12">
   <SectionHeading n={11}>Lexical this, Explained</SectionHeading>
   <div className="my-4 grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Traditional function</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400"><Code>this</Code> is decided at <Mark>call time</Mark> — by how the function is called.</div>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Arrow function</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400"><Code>this</Code> is decided at <Mark>creation time</Mark> — taken from the enclosing scope.</div>
    </div>
   </div>
   <Para>In short: an arrow function looks <Mark>outside</Mark> for <Code>this</Code> instead of creating its own.</Para>
  </section>

  {/* 12. When not to use */}
  <section className="mb-12">
   <SectionHeading n={12}>When NOT to Use Arrow Functions</SectionHeading>
   <Para>Don&apos;t use them as object methods that need <Code>this</Code> — use the method shorthand instead:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// avoid\nconst user = {\n  name: "Sachin",\n  greet: () => console.log(this.name), // wrong this\n};\n\n// prefer\nconst user = {\n  name: "Sachin",\n  greet() {\n    console.log(this.name); // correct\n  },\n};` }]} defaultLanguage="javascript" id="af-avoid" />
   <Callout tone="info">Also avoid them for constructors and for methods on prototypes — anywhere a dynamic <Code>this</Code> is required.</Callout>
  </section>

  {/* 13. React + TS + Java */}
  <section className="mb-12">
   <SectionHeading n={13}>React, TypeScript &amp; Java</SectionHeading>
   <Para>Modern React components are almost always arrow functions:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const UserCard = () => {\n  return <div>User</div>;\n};` }]} defaultLanguage="javascript" id="af-react" />
   <CodeBlock snippets={[{ language: 'typescript', code: `const add = (a: number, b: number): number => a + b;` }]} defaultLanguage="typescript" id="af-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `// Java lambda — conceptually the same\n(x, y) -> x + y` }]} defaultLanguage="java" id="af-java" />
  </section>

  {/* 14. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={14}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Arrow as object method">It captures the outer <Code>this</Code>, not the object — use method shorthand.</Callout>
   <Callout tone="warning" title="Returning an object literal">Wrap it in parentheses: <Code>() =&gt; ({`{ ... }`})</Code>.</Callout>
   <Callout tone="warning" title="Assuming its own this">Arrow functions never create their own <Code>this</Code> (or <Code>arguments</Code>).</Callout>
   <Callout tone="warning" title="Implicit vs explicit return">Braces mean you must write <Code>return</Code>; no braces means the expression is returned.</Callout>
  </section>

  {/* 15. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={15}>Real Interview Questions</SectionHeading>
   <QA q="What is the difference between a regular function and an arrow function?">
    Arrow functions are shorter and use <Mark>lexical this</Mark> (inherited from the enclosing scope). They also have no own <Code>arguments</Code> object and can&apos;t be used as constructors.
   </QA>
   <QA q="What is lexical this?">
    <Code>this</Code> in an arrow function is fixed at definition time to the surrounding scope&apos;s <Code>this</Code>, rather than being set by how the function is called.
   </QA>
   <QA q="Why does an arrow function method log undefined?">
    Because it doesn&apos;t bind <Code>this</Code> to the object — it uses the outer scope&apos;s <Code>this</Code> (often the module/global), where <Code>name</Code> doesn&apos;t exist.
   </QA>
   <QA q="When should you NOT use an arrow function?">
    As object methods, prototype methods, or constructors — anywhere you need a dynamic <Code>this</Code>.
   </QA>
   <QA q="How do you implicitly return an object literal?">
    Wrap it in parentheses: <Code>() =&gt; ({`{ name: "Sachin" }`})</Code>.
   </QA>
  </section>

  {/* 16. Mental model */}
  <section className="mb-12">
   <SectionHeading n={16}>Mental Model</SectionHeading>
   <Para>A traditional function <Mark>creates its own this</Mark>. An arrow function <Mark>borrows this</Mark> from its parent scope. That single difference explains nearly every arrow-function gotcha.</Para>
  </section>

  {/* 17 & 18 */}
  <section className="mb-4">
   <SectionHeading n={17}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Arrow functions provide concise syntax and use lexical <code>this</code> — they inherit <code>this</code> from their surrounding scope instead of creating their own.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Most developers reach for arrow functions for the shorter syntax. Senior engineers know the real difference is lexical <Code>this</Code> — a concept that shows up constantly in interviews and production debugging.
   </Callout>
  </section>
 </div>
);

export default JSArrowFunctions;
